(() => {
  const selectors = {
    drawer: '[data-search-drawer]',
    form: '[data-predictive-search-form]',
    input: '[data-predictive-search-input]',
    results: '[data-predictive-search-results]',
    status: '[data-predictive-search-status]',
  };

  const routesRoot = window.Shopify?.routes?.root || '/';
  let predictiveSearchTimeout;
  let predictiveSearchController;

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function resourceList(title, items, renderer) {
    if (!items?.length) return '';
    return `
      <section class="md-search-drawer__group">
        <h3>${escapeHtml(title)}</h3>
        <div class="md-search-drawer__list">
          ${items.map(renderer).join('')}
        </div>
      </section>
    `;
  }

  /* suggest.json hands back plain URL strings, so image_url is unavailable and
     the raw CDN original comes through instead — several megapixels rendered
     into a 56px thumbnail. The CDN width parameter is the supported way to
     resize a URL string. */
  function cdnThumbnail(url, width = 120) {
    if (!url) return '';

    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  function productSuggestion(product) {
    const imageUrl = cdnThumbnail(product.featured_image?.url || product.image);
    return `
      <a class="md-search-drawer__product" href="${escapeHtml(product.url)}">
        <span class="md-search-drawer__product-image">
          ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(product.featured_image?.alt || product.title)}" width="120" height="120" loading="lazy">` : ''}
        </span>
        <span>
          <strong>${escapeHtml(product.title)}</strong>
          <small>${escapeHtml(product.type || product.vendor || '')}</small>
        </span>
      </a>
    `;
  }

  function textSuggestion(item) {
    return `
      <a class="md-search-drawer__result" href="${escapeHtml(item.url)}">
        ${escapeHtml(item.title || item.text || '')}
      </a>
    `;
  }

  function fullSearchUrl(query) {
    return `${routesRoot}search?q=${encodeURIComponent(query)}&type=product,page,article&options%5Bprefix%5D=last`;
  }

  /* aria-controls is the authoritative link between an input and its results,
     so the component works outside the drawer too. The drawer lookup stays as a
     fallback for any markup that has not been given the attribute. */
  function resultsContainerFor(input) {
    const controlledId = input.getAttribute('aria-controls');
    const controlled = controlledId ? document.getElementById(controlledId) : null;
    if (controlled) return controlled;

    return input.closest(selectors.drawer)?.querySelector(selectors.results) || null;
  }

  /* The results container is fully re-rendered on every keystroke, so it must
     not be a live region itself — that would announce the entire markup each
     time. A separate role="status" element receives a short localized summary
     instead. Passing a null count clears the announcement (hint state). */
  function announceResultCount(input, container, count) {
    const status =
      input.closest(selectors.drawer)?.querySelector(selectors.status) ||
      container?.parentElement?.querySelector(selectors.status) ||
      null;
    if (!status) return;

    if (count === null) {
      status.textContent = '';
      return;
    }

    const template = container?.dataset.resultsAnnouncement || '[count] results';
    status.textContent = template.replace('[count]', String(count));
  }

  function renderPredictiveFallback(input, message) {
    const container = resultsContainerFor(input);
    if (!container) return;

    const query = input.value.trim();
    const fallbackMessage =
      message || container.dataset.emptyLabel || 'No suggestions yet.';

    if (query.length < 2) {
      container.innerHTML = `<p class="md-search-drawer__hint">${escapeHtml(container.dataset.hint || '')}</p>`;
      announceResultCount(input, container, null);
      return;
    }

    container.innerHTML = `
      <p class="md-search-drawer__hint">${escapeHtml(fallbackMessage)}</p>
      <a class="md-search-drawer__all" href="${fullSearchUrl(query)}">${escapeHtml(container.dataset.viewAllLabel || 'View all results')}</a>
    `;
    announceResultCount(input, container, 0);
  }

  function renderPredictiveResults(input, data) {
    const container = resultsContainerFor(input);
    if (!container) return;

    const query = input.value.trim();
    const results = data?.resources?.results || {};
    const resultCount =
      (results.products?.length || 0) +
      (results.queries?.length || 0) +
      (results.collections?.length || 0) +
      (results.pages?.length || 0) +
      (results.articles?.length || 0);
    const html = [
      resourceList(
        container.dataset.productsLabel || 'Products',
        results.products,
        productSuggestion,
      ),
      resourceList(
        container.dataset.queriesLabel || 'Suggestions',
        results.queries,
        textSuggestion,
      ),
      resourceList(
        container.dataset.collectionsLabel || 'Collections',
        results.collections,
        textSuggestion,
      ),
      resourceList(
        container.dataset.pagesLabel || 'Pages',
        results.pages,
        textSuggestion,
      ),
      resourceList(
        container.dataset.articlesLabel || 'Articles',
        results.articles,
        textSuggestion,
      ),
    ].join('');

    if (!html) {
      renderPredictiveFallback(input);
      return;
    }

    container.innerHTML = `${html}<a class="md-search-drawer__all" href="${fullSearchUrl(query)}">${escapeHtml(container.dataset.viewAllLabel || 'View all results')}</a>`;
    announceResultCount(input, container, resultCount);
  }

  async function fetchPredictiveResults(input) {
    const query = input.value.trim();
    const container = resultsContainerFor(input);
    if (!container) return;

    if (query.length < 2) {
      predictiveSearchController?.abort();
      container.innerHTML = `<p class="md-search-drawer__hint">${escapeHtml(container.dataset.hint || '')}</p>`;
      announceResultCount(input, container, null);
      return;
    }

    predictiveSearchController?.abort();
    const controller = new AbortController();
    predictiveSearchController = controller;
    const url = `${routesRoot}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article,query&resources[limit]=4&resources[limit_scope]=each`;
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) throw new Error('Predictive search failed');
    if (predictiveSearchController !== controller) return;
    renderPredictiveResults(input, await response.json());
  }

  function schedulePredictiveSearch(input) {
    window.clearTimeout(predictiveSearchTimeout);
    predictiveSearchTimeout = window.setTimeout(() => {
      fetchPredictiveResults(input).catch((error) => {
        if (error.name === 'AbortError') return;
        renderPredictiveFallback(input);
      });
    }, 180);
  }

  document.addEventListener('input', (event) => {
    const input = event.target.closest(selectors.input);
    if (!input) return;
    schedulePredictiveSearch(input);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest(selectors.form);
    if (!form) return;

    const input = form.querySelector(selectors.input);
    if (!input?.value.trim()) {
      event.preventDefault();
    }
  });

  window.MedistikPredictiveSearch = {
    renderFallback: renderPredictiveFallback,
    renderResults: renderPredictiveResults,
    search: fetchPredictiveResults,
  };
})();
